/**
 * clearAppCaches – purge stale cached application data on schema changes.
 *
 * Instead of clearing caches on every deploy (which would wipe offline PDFs
 * and PortalCache for every user), we use a `CACHE_SCHEMA_VERSION` that
 * is bumped ONLY when the underlying cache format changes — e.g. the
 * Svelte 4→5 upgrade changed how Service Worker precaches are structured.
 *
 * Normal incremental deploys leave the constant untouched, so user caches
 * are preserved.
 */

/**
 * Bump this when the cache format/schema changes.
 *
 * History:
 *   1 – original Svelte 4 app cache (implicit)
 *   2 – Svelte 5 upgrade: Service Worker precaches, app bundle format changed
 */
const CACHE_SCHEMA_VERSION = 2;

const LS_KEY = 'dpvp_cache_schema_version';

/**
 * Check whether the cached data on this device was stored under an older
 * schema version and needs to be purged.
 */
export function needsCacheMigration(): boolean {
  try {
    const stored = localStorage.getItem(LS_KEY);

    // No version stored at all — this could be:
    //   a) a Svelte 4 user whose browser has old caches (→ migrate)
    //   b) a brand-new browser with nothing cached (→ still migrate:
    //      clearAppCaches is a no-op on empty caches, so harmless)
    if (stored === null) return true;

    return Number(stored) < CACHE_SCHEMA_VERSION;
  } catch {
    return false;
  }
}

/**
 * Persist the current cache schema version so future visits can detect
 * whether a migration is needed.
 */
export function persistCacheSchemaVersion(): void {
  try {
    localStorage.setItem(LS_KEY, String(CACHE_SCHEMA_VERSION));
  } catch {
    // best-effort
  }
}

/**
 * Nuke every application cache that could serve stale content.
 *
 * Clears:
 *  – Service-worker Cache Storage (all named caches)
 *  – PortalCache Dexie / IndexedDB database
 *  – PDF cache IndexedDB database
 *  – Known localStorage stamps that may reference old cache keys
 */
export async function clearAppCaches(): Promise<void> {
  const results: string[] = [];

  // 1. Service-worker Cache Storage – blast every named cache
  if ('caches' in globalThis) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
      results.push(`SW caches: deleted ${keys.length} cache(s)`);
    } catch (err) {
      results.push(`SW caches: error – ${err}`);
    }
  }

  // 2. PortalCache (Dexie / IndexedDB) – delete the whole database
  if ('indexedDB' in globalThis) {
    try {
      indexedDB.deleteDatabase('PortalCache');
      indexedDB.deleteDatabase('pdf-cache');
      results.push('IndexedDB: PortalCache + pdf-cache databases deleted');
    } catch (err) {
      results.push(`IndexedDB: error – ${err}`);
    }
  }

  // 3. Known localStorage keys that may reference old cache entries
  try {
    const knownLocalStorageKeys = [
      'remoteConfigRetryCount',
      'remoteConfigLastRetry',
    ];
    for (const key of knownLocalStorageKeys) {
      localStorage.removeItem(key);
    }
    results.push('localStorage: stale keys removed');
  } catch {
    // best-effort
  }

  console.log('[clearAppCaches]', results.join(' │ '));
}
